import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAppSelector } from 'reduxTyped'
import { BillingServiceInterface } from 'common/billing/billingService/interfaces'
import { getIsPaywallShown } from 'common/billing/entities/upgradeRequest/selectors'
import { getPageInfo } from 'common/billing/helpers/getPageInfo'
import { UpgradeSource } from 'common/billing/interfaces/upgradeSource'
import { getCurrentAccountID } from 'common/core/selectors/appSelectors'
import { useDidMountEffect } from 'utils/commonHooks'

interface UseUpgradeDeeplinkListenerOptions {
  billing: BillingServiceInterface
}

export const useUpgradeDeeplinkListener = ({ billing }: UseUpgradeDeeplinkListenerOptions) => {
  const accountId = useAppSelector(getCurrentAccountID)
  const {
    isOnUpgradePage,
    sourceParam,
    productsParam,
    addOnsParam,
    titleParam,
    abilitiesParam,
    returnParam,
  } = getPageInfo()
  const navigate = useNavigate()

  // The paywall modal owns its own post-close redirect (see setPaywallShown
  // listener in upgradeRequestActions.ts). That listener only fires if the
  // modal actually opened, so it never runs when requestUpgrade resolves
  // instantly because the requested product is already active — this ref
  // tracks that so we only redirect here for the "never opened" case.
  const isPaywallShown = useAppSelector(getIsPaywallShown)
  const wasPaywallShownRef = useRef(false)
  useEffect(() => {
    if (isPaywallShown) {
      wasPaywallShownRef.current = true
    }
  }, [isPaywallShown])

  useDidMountEffect(() => {
    if (isOnUpgradePage) {
      const hasRequestedAnyProduct = Boolean(productsParam) || Boolean(addOnsParam)

      billing
        .requestUpgrade({
          source: sourceParam || UpgradeSource.DEEPLINK,
          products: productsParam ? productsParam : undefined,
          addOns: addOnsParam ? addOnsParam : undefined,
          abilities: abilitiesParam,
          strictRequest: hasRequestedAnyProduct,
          title: titleParam || undefined,
          isDeepLink: true,
        })
        .then((success) => {
          const getNextURL = () => {
            if (returnParam) {
              return returnParam
            }

            return accountId ? `/${accountId}/settings#subscriptions` : ''
          }

          if (success && !wasPaywallShownRef.current) {
            navigate(getNextURL())
          }
        })
    }
  })
}
